export interface DDSketchOptions {
  /** Relative accuracy α: every quantile is returned within ±α of the truth. */
  relativeAccuracy?: number;
}

/**
 * DDSketch — a mergeable sketch for streaming quantiles (p50, p95, p99, …) with
 * a *relative* error guarantee. Feed it a value stream and ask for any quantile;
 * the answer is within a factor of (1 ± α) of the true value, using memory that
 * grows only with the logarithm of the value range, not the number of items.
 *
 * The trick: bucket values geometrically. With γ = (1 + α)/(1 − α), value v lands
 * in bucket i = ⌈log_γ v⌉, which covers (γ^{i−1}, γ^i]. Reporting the bucket's
 * representative value 2γ^i/(γ+1) is guaranteed within α relative error of every
 * value it could contain. Because buckets are shared boundaries, two sketches
 * merge by simply adding their bucket counts — so you can keep a sketch per host
 * and combine them for a global p99, with no re-scan.
 *
 * This implementation covers non-negative values (latencies, sizes, durations —
 * the overwhelmingly common case) with an exact zero bucket, and tracks the true
 * min and max so q=0 and q=1 are exact.
 */
export class DDSketch {
  readonly alpha: number;
  readonly gamma: number;
  private readonly logGamma: number;
  /** bucket index → count */
  readonly buckets = new Map<number, number>();
  zeroCount = 0;
  count = 0;
  min = Infinity;
  max = -Infinity;

  constructor(options: DDSketchOptions = {}) {
    const alpha = options.relativeAccuracy ?? 0.01;
    if (alpha <= 0 || alpha >= 1) throw new RangeError("relativeAccuracy must be in (0, 1)");
    this.alpha = alpha;
    this.gamma = (1 + alpha) / (1 - alpha);
    this.logGamma = Math.log(this.gamma);
  }

  private indexOf(v: number): number {
    return Math.ceil(Math.log(v) / this.logGamma);
  }

  /** Representative value for a bucket — within α relative error of its contents. */
  valueOfIndex(i: number): number {
    return (2 * Math.pow(this.gamma, i)) / (this.gamma + 1);
  }

  add(value: number, weight = 1): void {
    if (value < 0) throw new RangeError("DDSketch supports non-negative values");
    this.count += weight;
    if (value < this.min) this.min = value;
    if (value > this.max) this.max = value;
    if (value === 0) {
      this.zeroCount += weight;
      return;
    }
    const i = this.indexOf(value);
    this.buckets.set(i, (this.buckets.get(i) ?? 0) + weight);
  }

  /**
   * The value at quantile q ∈ [0, 1], within ±α relative error.
   * q=0 returns the exact minimum; q=1 the exact maximum.
   */
  quantile(q: number): number {
    if (this.count === 0) return NaN;
    if (q <= 0) return this.min;
    if (q >= 1) return this.max;

    const rank = q * (this.count - 1);
    let cumulative = this.zeroCount;
    if (rank < cumulative) return 0;

    const keys = [...this.buckets.keys()].sort((a, b) => a - b);
    for (const k of keys) {
      cumulative += this.buckets.get(k)!;
      if (rank < cumulative) return this.valueOfIndex(k);
    }
    return this.max;
  }

  /** Merge another sketch (same accuracy) into this one — a stream union. */
  merge(other: DDSketch): void {
    if (other.gamma !== this.gamma)
      throw new Error("cannot merge DDSketches with different relative accuracy");
    this.count += other.count;
    this.zeroCount += other.zeroCount;
    this.min = Math.min(this.min, other.min);
    this.max = Math.max(this.max, other.max);
    for (const [k, c] of other.buckets) {
      this.buckets.set(k, (this.buckets.get(k) ?? 0) + c);
    }
  }

  /** Distinct buckets in use — this is what the sketch actually stores. */
  bucketCount(): number {
    return this.buckets.size + (this.zeroCount > 0 ? 1 : 0);
  }

  /** Buckets as {value, count}, sorted by value — for visualisation. */
  histogram(): Array<{ value: number; count: number }> {
    const out: Array<{ value: number; count: number }> = [];
    if (this.zeroCount > 0) out.push({ value: 0, count: this.zeroCount });
    const keys = [...this.buckets.keys()].sort((a, b) => a - b);
    for (const k of keys) out.push({ value: this.valueOfIndex(k), count: this.buckets.get(k)! });
    return out;
  }
}
